<cue-modal [modal]="modal" class="notch-fix" data-cy="assist-new-reservation-dialog">
  <cue-modal-header
    [title]="title() | transloco"
    [emitClose]="dialogData?.allowCompact || modal === null"
    (headerClose)="closeDialog()"
  />

  <cue-modal-body>
    <section class="new-reservation-dialog" [attr.data-mode]="mode()" [attr.data-state]="uiState()">
      @switch (uiState()) {
        @case ('loading') {
          <div class="new-reservation-dialog__state">
            <cue-loading />
            <p>{{ translation.newReservationUi.state.loading | transloco }}</p>
          </div>
        }
        @case ('error') {
          <div class="new-reservation-dialog__state new-reservation-dialog__state--error">
            <h2>{{ translation.newReservationUi.state.errorTitle | transloco }}</h2>
            <p>{{ loadErrorMessage() | transloco }}</p>
          </div>
        }
      }

      @if (saveFailed()) {
        <p class="new-reservation-dialog__save-error">
          {{ translation.newReservationUi.state.saveFailed | transloco }}
        </p>
      }

      @if (uiState() !== 'loading' && uiState() !== 'error') {
        <form
          class="new-reservation-dialog__sections"
          [formGroup]="reservationForm"
          [attr.aria-label]="translation.newReservationUi.plannedSections | transloco"
        >
          <section class="new-reservation-dialog__section">
            <header class="new-reservation-dialog__section-header">
              <h2>{{ translation.newReservationUi.sections.title | transloco }}</h2>
            </header>

            @if (uiState() === 'edit') {
              <assist-textbox
                data-cy="assist-new-reservation-subject"
                [formControl]="reservationForm.controls.subject"
                [showClear]="true"
                [iconBefore]="icons.cuePencil"
                [placeholder]="translation.newReservationUi.title.subjectPlaceholder | transloco"
              />

              @if (reservationForm.controls.subject.touched && reservationForm.controls.subject.invalid) {
                <p class="new-reservation-dialog__validation">
                  {{ translation.newReservationUi.title.subjectRequired | transloco }}
                </p>
              }
            } @else {
              <span data-cy="assist-new-reservation-readonly-subject" class="new-reservation-dialog__readonly-value">
                {{ formRawValue().subject }}
              </span>
            }

            @if (uiState() === 'edit') {
              <p class="new-reservation-dialog__hint">
                {{ translation.newReservationUi.title.subjectHelp | transloco }}
              </p>
              <p class="new-reservation-dialog__hint">
                {{ translation.newReservationUi.title.placeholderHelp | transloco: { placeholders: placeholderList } }}
              </p>
            }
          </section>

          <section class="new-reservation-dialog__section">
            <header class="new-reservation-dialog__section-header">
              <h2>{{ translation.newReservationUi.sections.timeSettings | transloco }}</h2>
            </header>

            @if (uiState() === 'edit') {
              @if (supportsAllDayEvents()) {
                <div class="new-reservation-dialog__checkbox-row">
                  <assist-checkbox
                    [formControl]="reservationForm.controls.allDay"
                    [title]="translation.newReservationUi.time.allDayEvent | transloco"
                  />
                </div>
              }

              <div class="new-reservation-dialog__time-grid">
                <div class="new-reservation-dialog__field">
                  <label>{{ translation.newReservationUi.time.start | transloco }}</label>
                  <cue-date-time-separate-info
                    data-cy="assist-new-reservation-start"
                    [dateOnly]="formUsesAllDayDateOnly()"
                    [dateTime]="formRawValue().start"
                    [type]="'from'"
                    [isStart]="true"
                    [editable]="true"
                    (edited)="onStartChanged($event)"
                  />
                </div>

                <div class="new-reservation-dialog__field">
                  <label>{{ translation.newReservationUi.time.end | transloco }}</label>
                  <cue-date-time-separate-info
                    data-cy="assist-new-reservation-end"
                    [dateOnly]="formUsesAllDayDateOnly()"
                    [dateTime]="formRawValue().end"
                    [min]="formRawValue().start"
                    [type]="'to'"
                    [isStart]="false"
                    [editable]="true"
                    (edited)="onEndChanged($event)"
                  />
                </div>
              </div>

              @if (repetitiveEventsEnabled()) {
                <div class="new-reservation-dialog__recurrence">
                  <h3>{{ translation.newReservationUi.time.recurrence | transloco }}</h3>

                  @if (supportsSeriesScopeSwitch()) {
                    <assist-checkbox
                      [formControl]="reservationForm.controls.editWholeSeries"
                      [title]="translation.newReservationUi.time.editWholeSeries | transloco"
                    />
                  }

                  @if (canEditRecurrencePattern()) {
                    <assist-recurrence-editor
                      [start]="formRawValue().start"
                      [supportedPatternTypes]="repetitiveEventOptions().supportedPatternTypes"
                      [formControl]="$any(reservationForm.controls.recurrence)"
                    />
                  } @else if (formRawValue().recurrence) {
                    <p class="new-reservation-dialog__hint">
                      {{ translation.newReservationUi.time.recurrenceConfigured | transloco }}
                    </p>
                  }
                </div>
              }
            } @else {
              <div data-cy="assist-new-reservation-readonly-range" class="new-reservation-dialog__readonly-range">
                @if (dateRange().kind === 'allDay') {
                  <span>{{ translation.newReservationUi.time.allDayEvent | transloco }}</span>
                  <strong>{{ formRawValue().start | dateFormat }} - {{ formRawValue().end | dateFormat }}</strong>
                } @else {
                  <strong
                    >{{ formRawValue().start | dateTimeFormat }} - {{ formRawValue().end | dateTimeFormat }}</strong
                  >
                }
              </div>
            }
          </section>

          <section class="new-reservation-dialog__section">
            <header class="new-reservation-dialog__section-header">
              <h2>{{ translation.newReservationUi.sections.locationAndOnline | transloco }}</h2>
            </header>

            @if (resourceRows().length === 0) {
              <p class="new-reservation-dialog__hint">
                {{ translation.newReservationUi.location.noLocationSelected | transloco }}
              </p>
            } @else {
              <div class="new-reservation-dialog__resource-list">
                @for (row of resourceRows(); track row.resource.idForFiltering) {
                  <assist-new-reservation-location-row
                    [row]="row"
                    (changeLocation)="openResourcePicker($event)"
                    (removeLocation)="removeResource($event)"
                  />
                }
              </div>
            }

            @if (uiState() === 'edit') {
              <assist-new-reservation-location-picker
                [active]="resourcePickerActive() && resourcePickerOrigin() === 'location'"
                [items]="resourcePickerItems()"
                [loading]="resourcesLoading()"
                [searchValue]="resourcePickerSearchInput()"
                (addLocation)="openResourcePicker()"
                (closePicker)="closeResourcePicker()"
                (resourceSelected)="selectResourcePickerResource($event)"
                (searchChange)="onResourcePickerFilterChange($event)"
                (scrollEnd)="onResourcePickerScrollEnd()"
              />
            }

            @if (showOnlineMeetingControls()) {
              <div class="new-reservation-dialog__online-meeting">
                <h3>{{ translation.newReservationUi.location.onlineMeeting | transloco }}</h3>

                @if (uiState() === 'edit') {
                  <assist-dropdownlist
                    [formControl]="reservationForm.controls.onlineMeetingProvider"
                    [data]="onlineMeetingOptions()"
                    textField="text"
                    valueField="value"
                    [valuePrimitive]="true"
                    [popupSettings]="{ appendTo: 'component' }"
                  />
                } @else {
                  <span class="new-reservation-dialog__readonly-value">
                    {{ selectedOnlineMeetingLabel() }}
                  </span>
                }
              </div>
            }
          </section>

          <section class="new-reservation-dialog__section">
            <header class="new-reservation-dialog__section-header">
              <h2>{{ translation.newReservationUi.sections.description | transloco }}</h2>
            </header>

            @if (uiState() === 'edit') {
              <div class="new-reservation-dialog__description-editor">
                <ng-container *quillLoad>
                  <quill-editor
                    cueRtl
                    defaultEmptyValue=""
                    [formControl]="reservationForm.controls.bodyHtml"
                    [modules]="descriptionEditorModules"
                    [styles]="descriptionEditorStyles"
                    class="new-reservation-dialog__quill-editor"
                  />
                </ng-container>

                @if (formRawValue().bodyHtml?.length) {
                  <button
                    type="button"
                    class="new-reservation-dialog__description-clear"
                    [attr.aria-label]="translation.newReservationUi.description.clear | transloco"
                    [title]="translation.newReservationUi.description.clear | transloco"
                    (click)="clearDescription()"
                  >
                    <cue-icon [icon]="icons.cueClose" theme="error-500" />
                  </button>
                }
              </div>

              <p class="new-reservation-dialog__hint">
                {{
                  translation.newReservationUi.description.placeholderHelp
                    | transloco: { placeholders: placeholderList }
                }}
              </p>
            } @else if (formRawValue().bodyHtml?.trim()) {
              <div class="new-reservation-dialog__description-readonly" [innerHTML]="formRawValue().bodyHtml"></div>
            } @else {
              <p class="new-reservation-dialog__hint">
                {{ translation.newReservationUi.description.emptyReadonly | transloco }}
              </p>
            }
          </section>

          <section class="new-reservation-dialog__section">
            <header class="new-reservation-dialog__section-header">
              <h2>{{ translation.newReservationUi.sections.administrationAndAttendees | transloco }}</h2>
            </header>

            <div class="new-reservation-dialog__field new-reservation-dialog__field--narrow">
              <label>{{ translation.newReservationUi.administration.showAsLabel | transloco }}</label>

              @if (uiState() === 'edit' && selectedResourceIds().length === 0) {
                <assist-dropdownlist
                  [formControl]="reservationForm.controls.showAs"
                  [data]="showAsOptions()"
                  valueField="value"
                  textField="text"
                  [popupSettings]="{ appendTo: 'component' }"
                >
                  <ng-template itemTemplate let-dataItem>
                    {{ dataItem?.text }}
                  </ng-template>
                  <ng-template valueTemplate let-dataItem>
                    {{ dataItem?.text }}
                  </ng-template>
                </assist-dropdownlist>
              } @else {
                <span
                  class="new-reservation-dialog__readonly-value new-reservation-dialog__readonly-value--showas"
                  [class.new-reservation-dialog__readonly-value--free]="
                    formRawValue().showAs.value === 'free' || formRawValue().showAs.value === 'transparent'
                  "
                  [class.new-reservation-dialog__readonly-value--busy]="
                    formRawValue().showAs.value === 'busy' || formRawValue().showAs.value === 'opaque'
                  "
                  [class.new-reservation-dialog__readonly-value--tentative]="
                    formRawValue().showAs.value === 'tentative'
                  "
                  [class.new-reservation-dialog__readonly-value--oof]="formRawValue().showAs.value === 'oof'"
                  [class.new-reservation-dialog__readonly-value--working-elsewhere]="
                    formRawValue().showAs.value === 'workingelsewhere' ||
                    formRawValue().showAs.value === 'workingElsewhere'
                  "
                >
                  {{ formRawValue().showAs.text }}
                </span>
              }
            </div>

            <div class="new-reservation-dialog__attendees">
              @if (organizerAttendee(); as organizer) {
                <div class="new-reservation-dialog__attendee-group">
                  <h3>{{ translation.newReservationUi.administration.organizer | transloco }}</h3>

                  <assist-new-reservation-attendee-item
                    [attendee]="organizer"
                    [editable]="uiState() === 'edit'"
                    [participationOptions]="participationOptions()"
                    (participationChange)="setAttendeeParticipation($event.email, $event.participation)"
                    (removeAttendee)="removeAttendee($event)"
                  />
                </div>
              }

              <div class="new-reservation-dialog__attendee-group">
                <h3>{{ translation.newReservationUi.administration.attendees | transloco }}</h3>

                @if (regularAttendees().length === 0) {
                  <p class="new-reservation-dialog__hint">
                    {{ translation.newReservationUi.administration.noAttendees | transloco }}
                  </p>
                } @else {
                  <div class="new-reservation-dialog__attendee-list">
                    @for (attendee of regularAttendees(); track attendee.email) {
                      <assist-new-reservation-attendee-item
                        [attendee]="attendee"
                        [editable]="uiState() === 'edit'"
                        [participationOptions]="participationOptions()"
                        (participationChange)="setAttendeeParticipation($event.email, $event.participation)"
                        (removeAttendee)="removeAttendee($event)"
                      />
                    }
                  </div>
                }

                @if (uiState() === 'edit') {
                  <assist-new-reservation-attendee-picker
                    [active]="attendeePickerActive()"
                    [value]="attendeePickerValue()"
                    [customEmail]="attendeePickerCustomEmail()"
                    (openPicker)="openAttendeePicker()"
                    (valueChange)="onAttendeePickerValueChange($event)"
                    (filterValueChange)="onAttendeePickerFilterChange($event)"
                    (addCustomEmail)="addAttendeeByEmail($event)"
                  />
                }
              </div>
            </div>
          </section>

          <section class="new-reservation-dialog__section">
            <header class="new-reservation-dialog__section-header">
              <h2>{{ translation.newReservationUi.sections.locationDetailAndOrders | transloco }}</h2>
            </header>

            @if (locationDetailOrderRows().length === 0) {
              <p class="new-reservation-dialog__hint">
                {{ translation.newReservationUi.locationDetails.noLocationSelected | transloco }}
              </p>

              @if (uiState() === 'edit') {
                <assist-new-reservation-location-picker
                  [active]="resourcePickerActive() && resourcePickerOrigin() === 'emptyDetails'"
                  [items]="resourcePickerItems()"
                  [loading]="resourcesLoading()"
                  [searchValue]="resourcePickerSearchInput()"
                  (addLocation)="openResourcePicker(null, 'emptyDetails')"
                  (closePicker)="closeResourcePicker()"
                  (resourceSelected)="selectResourcePickerResource($event)"
                  (searchChange)="onResourcePickerFilterChange($event)"
                  (scrollEnd)="onResourcePickerScrollEnd()"
                />
              }
            } @else {
              <div class="new-reservation-dialog__location-accordions">
                @for (row of locationDetailOrderRows(); track row.resource.idForFiltering) {
                  <assist-new-reservation-location-detail-accordion-item
                    [row]="row"
                    [editable]="uiState() === 'edit'"
                    [eventStart]="locationDetailEventStart()"
                    [ordersEnabled]="configService.value.enableOrders"
                    [previousOrdersLoading]="previousOrders.isLoading()"
                    (toggle)="toggleLocationDetail($event)"
                    (addingOrderItemChange)="setAddingOrderItem($event.resourceIdForFiltering, $event.adding)"
                    (orderItemNoteChange)="
                      setOrderItemNote($event.resourceIdForFiltering, $event.clientId, $event.note)
                    "
                    (orderItemRemove)="removeOrderItem($event.resourceIdForFiltering, $event.clientId)"
                    (orderNoteChange)="setOrderNote($event.resourceIdForFiltering, $event.note)"
                    (orderServiceSelected)="onOrderServiceSelected($event.resourceIdForFiltering, $event.item)"
                  />
                }
              </div>
            }
          </section>
        </form>
      }
    </section>
  </cue-modal-body>

  <cue-modal-footer class="new-reservation-dialog__footer">
    <cue-button
      type="secondary"
      [icon]="icons.cueClose"
      (click)="closeDialog()"
      [smallResponsive]="true"
      data-cy="assist-new-reservation-dialog-close"
    >
      {{ translation.newReservationUi.actions.close | transloco }}
    </cue-button>

    @if (showConfirmAction()) {
      <cue-button [icon]="icons.cueCheckCircle" (click)="onConfirm()" [smallResponsive]="true">
        {{ translation.dashboard.confirm | transloco }}
      </cue-button>
    }

    @if (showFinishAction()) {
      <cue-button [icon]="icons.cueFinish" (click)="onFinish()" [smallResponsive]="true">
        {{ translation.finishEvent.finish | transloco }}
      </cue-button>
    }

    @if (showDeleteActions()) {
      @if (isOccurrenceLikeEvent()) {
        <cue-button type="delete" [icon]="icons.cueDelete" (click)="onDeleteOccurrence()" [smallResponsive]="true">
          {{ translation.newReservationUi.actions.deleteOccurrence | transloco }}
        </cue-button>
        <cue-button type="delete" [icon]="icons.cueDelete" (click)="onDeleteSeries()" [smallResponsive]="true">
          {{ translation.newReservationUi.actions.deleteSeries | transloco }}
        </cue-button>
      } @else if (reservation()?.type === 'seriesMaster') {
        <cue-button type="delete" [icon]="icons.cueDelete" (click)="onDeleteSeries()" [smallResponsive]="true">
          {{ translation.newReservationUi.actions.deleteSeries | transloco }}
        </cue-button>
      } @else {
        <cue-button type="delete" [icon]="icons.cueDelete" (click)="onDelete()" [smallResponsive]="true">
          {{ translation.newReservationUi.actions.deleteEvent | transloco }}
        </cue-button>
      }
    }

    @if (showCancelParticipation()) {
      <cue-button type="delete" [icon]="icons.cueDelete" (click)="cancelParticipation()" [smallResponsive]="true">
        {{ translation.newReservationUi.actions.cancelParticipation | transloco }}
      </cue-button>
    }

    @if (canSwitchToEdit()) {
      <cue-button type="primary" [icon]="icons.cuePencil" (click)="switchToEdit()" [smallResponsive]="true">
        {{ translation.newReservationUi.actions.edit | transloco }}
      </cue-button>
    }

    @if (uiState() === 'edit') {
      <div
        class="new-reservation-dialog__save-action"
        [class.new-reservation-dialog__save-action--with-warning]="saveDisableReasons().length > 0"
      >
        @if (saveDisableReasons().length > 0) {
          <ng-template #saveDisableReasonsTemplate>
            <div class="new-reservation-dialog__tooltip-content">
              @for (reason of saveDisableReasons(); track reason.level + reason.message) {
                <p>{{ reason.message }}</p>
              }
            </div>
          </ng-template>

          <cue-icon
            class="new-reservation-dialog__save-warning"
            [class.new-reservation-dialog__save-warning--warning]="!hasErrorSaveDisableReason()"
            [icon]="icons.cueExclamationCircle"
            cueTooltipCommonType="alert"
            [cueTooltip]="saveDisableReasonsTemplate"
          />
        }

        <cue-button
          data-cy="assist-new-reservation-save"
          type="primary"
          [icon]="icons.cueShuffle"
          [disabled]="!canSave()"
          (click)="save()"
          [smallResponsive]="true"
        >
          {{ translation.newReservationUi.actions.save | transloco }}
        </cue-button>
      </div>
    }
  </cue-modal-footer>
</cue-modal>
